import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { ensureSubject } from '../utils/taxonomy';
import SearchableSelect from './SearchableSelect';
import Select from './Select';
import Icon from '@mdi/react';
import { mdiArrowRight, mdiCheck, mdiClose, mdiPencil, mdiDelete } from '@mdi/js';

/**
 * Reusable import-mappings editor.
 *
 * Props:
 *  - mappings:      object { [key]: { original, type, subject_id, account_id } }
 *  - onChange:      (nextMappings) => void   — called on add/edit/delete
 *  - accounts:      array of account objects
 *  - subjects:       array of subject objects
 *  - onSubjectCreated: optional (subject) => void — called when a new subject
 *                    is created on the fly from a free-text combo value
 *
 * Used by ImportTransactions (inline) and ManageImportMappings (page).
 */
const ImportMappings = ({ mappings, onChange, accounts, subjects, onSubjectCreated }) => {
  const [newMapping, setNewMapping] = useState({
    original: '',
    mappedOption: null,
    type: 'subject',
  });
  const [editType, setEditType] = useState('subject');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editOriginal, setEditOriginal] = useState('');
  const [editMappedOption, setEditMappedOption] = useState(null);

  const resolveMappedOption = async (option, type) => {
    if (type === 'subject' && option && option.isNew) {
      const ensured = await ensureSubject(option.label);
      if (onSubjectCreated) {
        onSubjectCreated({ id: ensured.value, name: ensured.label });
      }
      return ensured;
    }
    return option;
  };

  const handleAddMapping = async () => {
    if (!newMapping.original || !newMapping.mappedOption) return;

    const mapped = await resolveMappedOption(newMapping.mappedOption, newMapping.type);

    const numericKeys = Object.keys(mappings)
      .filter((k) => !isNaN(k))
      .map(Number);
    const nextKey = numericKeys.length > 0
      ? (Math.max(...numericKeys) + 1).toString()
      : '0';

    const newEntry = {
      original: newMapping.original,
      type: newMapping.type,
      subject_id: newMapping.type === 'subject' ? mapped.value : null,
      account_id: newMapping.type === 'account' ? mapped.value : null,
    };

    onChange({ ...mappings, [nextKey]: newEntry });
    setNewMapping({ original: '', mappedOption: null, type: 'subject' });
  };

  const handleRemoveMapping = (index) => {
    const next = { ...mappings };
    delete next[index];
    onChange(next);
  };

  return (
    <div className="form-group mappings-container">
      <label>Subject &amp; Account Mappings</label>

      <div className="mappings-list">
        {Object.entries(mappings).map(([index, mapping]) => (
          <div className="form-group-row mapping-entry" key={index}>
            {editingIndex === index ? (
              <>
                <input
                  type="text"
                  value={editOriginal}
                  onChange={(e) => setEditOriginal(e.target.value)}
                  style={{ width: '200px' }}
                />

                <Icon path={mdiArrowRight} size={3} />

                <Select
                  options={[
                    { value: 'subject', label: 'Subject' },
                    { value: 'account', label: 'Account' },
                  ]}
                  onSelect={(option) => {
                    setEditType(option.value);
                    setEditMappedOption(null);
                  }}
                  value={editType}
                  style={{ width: '250px' }}
                />

                {editType === 'subject' ? (
                  <SearchableSelect
                    options={subjects.map((s) => ({ value: s.id, label: s.name }))}
                    onSelect={(option) => setEditMappedOption(option)}
                    placeholder="Map to subject"
                    value={editMappedOption && editMappedOption.value ? editMappedOption.value : null}
                    allowCreate={true}
                  />
                ) : (
                  <SearchableSelect
                    options={accounts.map((a) => ({ value: a.id, label: a.name }))}
                    onSelect={(option) => setEditMappedOption(option)}
                    placeholder="Map to account"
                    value={editMappedOption && editMappedOption.value ? editMappedOption.value : null}
                    allowCreate={false}
                  />
                )}

                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={async () => {
                    if (!editMappedOption) {
                      alert('Please select a subject or account');
                      return;
                    }
                    const mapped = await resolveMappedOption(editMappedOption, editType);
                    const updatedMapping = {
                      original: editOriginal,
                      type: editType,
                      subject_id: editType === 'subject' ? mapped.value : null,
                      account_id: editType === 'account' ? mapped.value : null,
                    };
                    onChange({ ...mappings, [index]: updatedMapping });
                    setEditingIndex(null);
                    setEditMappedOption(null);
                  }}
                >
                  <Icon path={mdiCheck} size={0.8} style={{ color: 'green' }} />
                </button>

                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    setEditMappedOption(null);
                    setEditingIndex(null);
                  }}
                >
                  <Icon path={mdiClose} size={0.8} style={{ color: 'red' }} />
                </button>
              </>
            ) : (
              <>
                <label style={{ textAlign: 'left', width: '30%', fontWeight: 'bold' }}>
                  {mapping.original}
                </label>

                <Icon path={mdiArrowRight} size={2} style={{ margin: '0 10px' }} />

                <label style={{ textAlign: 'left', width: '40%' }}>
                  {mapping.type === 'subject'
                    ? subjects.find((s) => s.id === mapping.subject_id)?.name || '(Unknown Subject)'
                    : 'Transfer to ' + (accounts.find((a) => a.id === mapping.account_id)?.name || '(Unknown Account)')}
                </label>

                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    setEditOriginal(mapping.original);
                    if (mapping.type === 'subject') {
                      const opt = subjects.find((s) => s.id === mapping.subject_id);
                      setEditMappedOption(opt ? { value: opt.id, label: opt.name } : null);
                    } else {
                      const opt = accounts.find((a) => a.id === mapping.account_id);
                      setEditMappedOption(opt ? { value: opt.id, label: opt.name } : null);
                    }
                    setEditType(mapping.type);
                    setEditingIndex(index);
                  }}
                >
                  <Icon path={mdiPencil} size={0.8} />
                </button>

                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => handleRemoveMapping(index)}
                >
                  <Icon path={mdiDelete} size={0.8} style={{ color: 'red' }} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="form-group-row mappings-add-row">
        <div className="form-group">
          <input
            type="text"
            placeholder="Original text"
            value={newMapping.original}
            onChange={(e) => setNewMapping({ ...newMapping, original: e.target.value })}
            style={{ width: '200px' }}
          />
        </div>

        <Select
          options={[
            { value: 'subject', label: 'Subject' },
            { value: 'account', label: 'Account' },
          ]}
          onSelect={(option) => setNewMapping({ ...newMapping, type: option.value })}
          value={newMapping.type}
          style={{ width: '100px' }}
        />

        {newMapping.type === 'subject' ? (
          <SearchableSelect
            options={subjects.map((s) => ({ value: s.id, label: s.name }))}
            onSelect={(option) => setNewMapping({ ...newMapping, mappedOption: option })}
            placeholder="Map to subject"
            value={newMapping.mappedOption}
            allowCreate={true}
          />
        ) : (
          <SearchableSelect
            options={accounts.map((a) => ({ value: a.id, label: a.name }))}
            onSelect={(option) => setNewMapping({ ...newMapping, mappedOption: option })}
            placeholder="Map to account"
            value={newMapping.mappedOption}
          />
        )}

        <button type="button" className="btn btn--secondary" onClick={handleAddMapping}>
          Add
        </button>
      </div>
    </div>
  );
};

export default ImportMappings;